import { For, Show, createSignal, type Component } from 'solid-js';

import {
  buyItem,
  RARITY_ORDER,
  sellItems,
  type InventoryItem,
  type ItemRarity,
} from '../net/protocol';
import type { GameStore } from '../state/store';
import { freeCells } from '../game/container';
import { Icon } from './Icon';
import { ItemGrid } from './ItemGrid';
import { Purse } from './Purse';
import { FloatingTooltip, ItemTooltipBody } from './ItemTooltip';
import { rarityClass } from './stash';
import {
  formatReroll,
  selectableUuids,
  sellableUpTo,
  sellableValueUpTo,
  stationInfo,
} from './stations';
import { VaultSection } from './VaultSection';

/**
 * One workshop's panel: what it is, what it can do, and honestly what it cannot.
 *
 * The design gives a station three functions — buy, scrap and craft. **Only scrap
 * exists**: there is no vendor stock, no gold and no recipes. So the other two render a
 * designed empty state naming exactly what is missing, which is the rule the whole HUD
 * follows: the rebirth modal shows no ledger rather than a plausible one, and the
 * apocalypse banner counts down to nothing rather than to an invented date.
 *
 * The five stations share one panel with five headings rather than being five
 * components, because their bodies are identical today — and the row of icons at the
 * top switches between them without closing, which is the point of the whole feature.
 */
/**
 * How each rarity ceiling is named on its button.
 *
 * Plural because the button sells a *set*, and `Record` over the closed union so a fifth rarity is a
 * compile error rather than a button with no label — the same rule `SHOT_SHAPES` follows.
 */
const RARITY_LABEL: Record<ItemRarity, string> = {
  NORMAL: 'Normals',
  MAGIC: 'Magic',
  RARE: 'Rares',
  UNIQUE: 'Everything',
};

export const StationPanel: Component<{ store: GameStore }> = (props) => {
  /** The offer the pointer is resting on, and what to hang its tooltip on. */
  const [hoveredOffer, setHoveredOffer] = createSignal<InventoryItem | null>(null);
  const [offerAnchor, setOfferAnchor] = createSignal<HTMLElement | undefined>();

  const info = () => stationInfo(props.store.station() ?? '');
  const items = () => props.store.inventory();

  /**
   * What the counter can actually charge against.
   *
   * **Carried gold plus the stash** (owner's call, 2026-08-13). The server spends the purse first
   * and draws the shortfall from the bank, so an offer is affordable when the two together cover
   * it — marking it *dear* against the purse alone would grey out things the player can buy, and
   * a client that disagrees with the server about affordability is worse than one that says
   * nothing.
   *
   * Reaching the bank at all is coherent because buying is town-only and the stash is in town.
   */
  const purse = () => props.store.gold();
  const banked = () => props.store.stashedGold();
  const spendable = () => purse() + banked();


  return (
    <Show when={props.store.menu() === 'station' && info()}>
      {(station) => (
        <div
          class="sheet hud-interactive"
          data-testid="station-panel"
          role="dialog"
          aria-modal="true"
          aria-label={station().name}
        >
          <div class="plate plate--riveted sheet__panel">
            <header class="sheet__head">
              <h2 class="sheet__title">{station().name}</h2>
              <button
                type="button"
                class="sheet__close"
                data-testid="station-close"
                onClick={() => props.store.closeMenu()}
                aria-label="Close"
              >
                ✕
              </button>
            </header>

            {/*
              **No row of other shops.**

              This used to carry a tab per workshop, so standing at the Forge showed five
              icons of places you were not. Asked for directly: *the top icons when in a
              shop, representing all other shops, should not be there.* The way between
              vendors is **Escape, then the next one's hotkey** — the town bar already
              prints every one of them, and a second way in from inside a panel meant the
              panel could teleport you somewhere while you were reading it.

              What is left is where you are, in the header above, and the domain below.
            */}
            <p class="station__domain" data-testid="station-domain">
              {station().domain}
            </p>

            {/* ── The vault, at the Arcane Vault and nowhere else ─────────── */}
            {/* The one station with a function of its own. It is here rather than on a
                key because the vault is a *place*: the server refuses every move
                touching it outside town, so an affordance the player could reach from a
                dungeon would spend most of a session saying no. See VaultSection. */}
            {/*
              **The stash, and only at the stash.** It used to hang off the Arcane Vault,
              which made a jewellery bench also the place every item you own lives —
              storing your gear and enchanting a ring are different errands.
            */}
            <Show when={station().kind === 'STASH'}>
              <VaultSection store={props.store} />
            </Show>

            {/* ── Scrap: the part that works ─────────────────────────────── */}
            {/*
              ── The counter: your bag on the left, the shelf on the right ────

              **Selling needs the bag on screen.** The bulk buttons above empty it
              wholesale, but choosing *this ring and not that one* means seeing what you
              are carrying — and the panel showed a shelf and a list of names with no
              inventory anywhere near it.

              The bag takes the larger share deliberately: it is a 20 × 8 grid against
              eight rows of text, so splitting the width evenly would squeeze the thing
              that needs the room and leave the shelf with space it cannot use.
            */}
            {/*
              ── The counter, at the trading stations and not at the stash ────

              **The stash keeps things; it does not buy or sell them.** Asked for directly:
              *the stash should only allow to store items or gold, not to buy or sell.* It
              is a different errand — a place you leave what you are not carrying — and a
              shelf of merchant stock beside the storage grid made it a shop that also has
              a cupboard.

              This is a *presentation* scope and it says so rather than implying more:
              server-side, `sellItems` and `buyItem` check `InHub` and nothing else, so
              trading is a town-wide capability rather than one attached to a fixture. A
              modified client selling "at the stash" is a client selling in town, which it
              is entitled to do. Hiding the counter here is about what this panel is *for*,
              not about what the server permits — and nothing is being guarded that would
              matter if it got through.
            */}
            <Show when={station().kind !== 'STASH'}>
            <div class="station__counter">
              <section class="station__section station__side" aria-label="Your bag">
                <h3 class="station__heading">
                  Carried · <span data-testid="counter-bag-free">{freeCells(props.store.bag())}</span>{' '}
                  free
                </h3>

                {/*
                  **Selling needs no selection.** Scrapping used to build one because it
                  was destructive and the player was choosing what to destroy; selling is
                  a trade, and making somebody tick thirteen boxes to empty a bag is the
                  chore a bulk interface exists to remove. Both honour the padlock.
                */}
                <div class="station__actions">
                  <button
                    type="button"
                    class="skills__button"
                    data-testid="sell-all-unlocked"
                    onClick={() => props.store.send(sellItems(selectableUuids(items())))}
                  >
                    Sell all unlocked
                  </button>
                </div>

                {/*
                  **One button per rarity ceiling, each selling that rarity and everything under it.**

                  Asked for directly. A ceiling rather than a floor because that is how a player thinks
                  about clearing a bag — *get rid of anything up to Rare* — and it makes each button's
                  outcome readable from its own label without consulting the one beside it.

                  **`Sell trash` was removed rather than kept beside these.** It sold everything below
                  Rare, which is exactly what `Magic` does here — two buttons with different names and
                  identical behaviour is worse than either alone, and the one that names its ceiling is
                  the one a player can predict.

                  The count and the gold are in the label, so the destructive-looking button says what
                  it will do before it is pressed. Zero disables it: a bulk button that consumes a
                  click and reports nothing is indistinguishable from a broken one, and the server
                  would refuse an empty batch with `NOTHING_SOLD` anyway.
                */}
                <div class="station__rarities" data-testid="sell-by-rarity">
                  <For each={RARITY_ORDER}>
                    {(rarity) => {
                      const uuids = () => sellableUpTo(items(), rarity);
                      const gold = () => sellableValueUpTo(items(), rarity);
                      return (
                        <button
                          type="button"
                          class={`skills__button station__rarity station__rarity--${rarity.toLowerCase()}`}
                          data-testid={`sell-upto-${rarity.toLowerCase()}`}
                          disabled={uuids().length === 0}
                          title={`Sell every unlocked ${RARITY_LABEL[rarity]} and below`}
                          onClick={() => props.store.send(sellItems(uuids()))}
                        >
                          {RARITY_LABEL[rarity]}
                          <span class="station__rarity-count">
                            {uuids().length} · {gold()}g
                          </span>
                        </button>
                      );
                    }}
                  </For>
                </div>
                <ItemGrid
                  store={props.store}
                  container={props.store.bag()}
                  testId="counter-bag"
                  // Click sells it. The shelf is right there and the gold readout is
                  // above it, so the outcome is visible in the same glance.
                  onActivate={(it) => props.store.send(sellItems([it.uuid]))}
                  tooltip={(it) => (
                    <>
                      <ItemTooltipBody
                        item={it}
                        attributes={props.store.character()?.attributes.total ?? null}
                      />
                      <p class="doll__tip-note">
                        {typeof it.sellValue === 'number' && !it.isLocked
                          ? 'Click to sell it.'
                          : it.isLocked
                            ? 'Locked. Alt-click to unlock it first.'
                            : 'This cannot be sold.'}
                      </p>
                    </>
                  )}
                />
              </section>

            <section class="station__section station__side station__side--shelf" aria-label="For sale">
              {/*
                **The purse, large, at the counter.** This is where gold is the constraint
                on what you can do — every price on the shelf below is measured against it —
                so it is the headline of this section rather than a figure inside the
                sentence about restocking.
              */}
              <div class="station__purse">
                <Purse gold={purse()} label="your gold" testId="station-gold" />
                {/*
                  **The bank, shown only when it is doing something.** A second figure on every
                  shop counter would be noise for the ordinary case where the purse covers the
                  price; it appears when there is something banked, because that is exactly when
                  a player needs to know their spending power is larger than the number above it.
                */}
                <Show when={banked() > 0}>
                  <p class="station__banked" data-testid="station-banked">
                    <Purse gold={banked()} small /> in the stash, spent after your purse
                  </p>
                </Show>
              </div>

              <h3 class="station__heading">
                For sale · new stock in{' '}
                <span data-testid="station-reroll">
                  {formatReroll(props.store.vendor().rerollInSec)}
                </span>
              </h3>

              <Show
                when={props.store.vendor().offers.length > 0}
                fallback={
                  <p class="sheet__seam" data-testid="station-no-stock">
                    The shelf is empty. New stock is rolled at your level on a timer.
                  </p>
                }
              >
                <ul class="station__items" data-testid="station-shelf">
                  <For each={props.store.vendor().offers}>
                    {(offer) => (
                      <li class="station__item">
                        <button
                          type="button"
                          class={`station__pick station__pick--${rarityClass(offer.item.rarity)}`}
                          classList={{ 'station__pick--dear': offer.price > spendable() }}
                          data-testid={`buy-${offer.id}`}
                          // Marked, never disabled: an item you cannot afford is exactly
                          // the one whose stats are worth reading, and the server refuses
                          // it anyway with a reason the HUD words.
                          onClick={() => props.store.send(buyItem(offer.id))}
                          // **A shop you cannot read is a shop you cannot use.** The row
                          // shows a name and a price; whether the thing is worth buying is
                          // in its affixes and its gate, which is what the bag's tooltip
                          // has always shown. Same component, so a shelf and a bag say the
                          // same things about the same item.
                          onPointerEnter={(e) => {
                            setHoveredOffer(offer.item);
                            setOfferAnchor(e.currentTarget);
                          }}
                          onPointerLeave={() => setHoveredOffer(null)}
                          onFocus={(e) => {
                            setHoveredOffer(offer.item);
                            setOfferAnchor(e.currentTarget);
                          }}
                          onBlur={() => setHoveredOffer(null)}
                        >
                          <Icon
                            class="station__glyph"
                            iconId={offer.item.iconId}
                            glyph={offer.item.glyph}
                          />
                          <span class="station__name">{offer.item.name}</span>
                          <span class="station__meta">iLvl {offer.item.itemLevel}</span>
                          <span class="station__value" data-testid={`price-${offer.id}`}>
                            {offer.price}g
                          </span>
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>

              {/* One tooltip for the whole shelf rather than one per row: only one can be
                  hovered, and a portal per offer is eight effects that are almost always
                  empty. Same arrangement `ItemGrid` uses. */}
              <FloatingTooltip
                anchor={offerAnchor()}
                when={hoveredOffer() !== null}
                testId="offer-tip"
              >
                <Show when={hoveredOffer()}>
                  {(it) => (
                    <ItemTooltipBody
                      item={it()}
                      attributes={props.store.character()?.attributes.total ?? null}
                    />
                  )}
                </Show>
              </FloatingTooltip>

              {/* Still a seam, and still labelled as one: crafting does not exist. */}
              <p class="sheet__seam" data-testid="station-materials">
                No recipes exist yet, so there is nothing to forge. When refining lands,{' '}
                {station().name} will work in {station().materials.toLowerCase()}. Today every
                workshop stocks and pays the same.
              </p>
            </section>
            </div>
            </Show>
          </div>
        </div>
      )}
    </Show>
  );
};
